<template>
    <div>
        <p v-if="!receiptList.length" class="text-center mt-5">No money receipts found for this order.</p>

        <div v-for="receipt in receiptList" :key="receipt.id" class="card general-card receipt-card">
            <div class="receipt-card-header">
                <div class="row align-items-center">
                    <div class="col-md-4 col-12">
                        <p class="mb-25 receipt-title">
                            <i class="fas fa-receipt me-25"></i> {{ receipt.bill_number }}
                        </p>
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">{{ readableDate(receipt.receipt_date) }}</p>
                    </div>
                    <div class="col-md-4 col-12">
                        <p class="mb-25">Customer: {{ receipt.contact_name }}</p>
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Applied Against: {{ appliedBillNumbers(receipt) }}</p>
                    </div>
                    <div class="col-md-4 col-12 d-flex justify-content-end align-items-center">
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="onClickPrint(receipt)">
                            <i class="fas fa-print"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="px-1 pb-1 pt-1">
                <div class="row align-items-center">
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Paid Amount</p>
                        <p class="mb-0 amount-value">{{ commaFormat(receipt.total_paid_amount) }}</p>
                    </div>
                    <div class="col-md-3 col-6">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Reference</p>
                        <p class="mb-0">{{ receipt.receipt_reference || '-' }}</p>
                    </div>
                    <div v-if="receipt.description" class="col-md-6 col-12">
                        <p class="mb-0" style="font-size: 12px; color: #6c757d">Note</p>
                        <p class="mb-0">{{ receipt.description }}</p>
                    </div>
                </div>

                <div v-if="generalsOf(receipt).length" class="table-responsive mt-25">
                    <table class="table table-bordered custom-table-padding mb-0">
                        <thead>
                            <tr>
                                <th>Invoice No</th>
                                <th class="text-right">Bill Amount</th>
                                <th class="text-right">Discount</th>
                                <th class="text-right">Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in generalsOf(receipt)" :key="row.id">
                                <td>{{ row.bill_number }}</td>
                                <td class="text-right">{{ commaFormat(row.bill_amount) }}</td>
                                <td class="text-right">{{ commaFormat(row.discount_amount) }}</td>
                                <td class="text-right">{{ commaFormat(row.paid_amount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <Loader v-if="printing"/>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import figureFormatter from '@/services/utils/figureFormatter'
import useDate from '@/services/utils/day'
import handlePurchase from '@/services/modules/purchase'
import handleCompany from '@/services/modules/company'
import moneyReceiptHelper from '@/services/utils/pdf/moneyReceiptHelper'
import Loader from '@/components/atom/LoaderComponent'

const props = defineProps({
    orderDetail: { type: Object, required: true }
})

const route = useRoute()
const { commaFormat } = figureFormatter()
const { formatToDdMmYy } = useDate()
const { fetchMoneyReceipt } = handlePurchase()
const { fetchCompanyInfo } = handleCompany()
const { generatePdf: generateMoneyReceiptPDF } = moneyReceiptHelper()

const printing = ref(false)
const companyInfo = ref({})

const receiptList = computed(() => {
    if (!props.orderDetail.money_receipts) return []
    return props.orderDetail.money_receipts
})

const generalsOf = (receipt) => {
    if (!receipt.general) return []
    return receipt.general
}

const appliedBillNumbers = (receipt) => {
    const rows = generalsOf(receipt)
    if (!rows.length) return ''
    return rows.map((row) => row.bill_number).join(', ')
}

const readableDate = (date) => {
    if (!date) return ''
    return formatToDdMmYy(String(date).split(' ')[0])
}

const companyQuery = () => {
    return `?company_id=${route.params.companyId}`
}

const loadCompanyInfo = async () => {
    if (companyInfo.value.id) return
    await fetchCompanyInfo(route.params.companyId).then((res) => {
        companyInfo.value = res.data
    })
}

const onClickPrint = async (receipt) => {
    if (!receipt.id) return
    printing.value = true
    await loadCompanyInfo()

    await fetchMoneyReceipt(receipt.id, companyQuery()).then((res) => {
        if (!res.status) return
        generateMoneyReceiptPDF(companyInfo.value, res.data)
    })

    printing.value = false
}
</script>

<style scoped>
.general-card {
    min-height: auto;
}
.receipt-card {
    overflow: hidden;
    margin-bottom: 1rem;
}
.receipt-card-header {
    padding: 0.85rem 1rem;
    background-color: rgba(40, 199, 111, 0.06);
    border-bottom: 1px solid rgba(40, 199, 111, 0.14);
}
.receipt-title {
    font-size: 15px;
    font-weight: 600;
}
.amount-value {
    font-size: 15px;
    font-weight: 700;
    color: #28c76f;
}
.fw-bold {
    font-weight: bold !important;
}
.custom-table-padding :deep(td),
.custom-table-padding :deep(th) {
    padding: 6px 10px;
    font-size: 13px;
}
</style>
